import React, { useState } from 'react';
import {
  CalendarBlank,
  Plus,
  Camera,
  Image as ImageIcon,
  X,
  ArrowsLeftRight,
} from '@phosphor-icons/react';

const PHOTO_CATEGORIES = [
  { key: 'food', label: 'Food & Drinks' },
  { key: 'drinks', label: 'Drinks' },
  { key: 'menu', label: 'Menu' },
  { key: 'interior', label: 'Venue' },
  { key: 'exterior', label: 'Exterior' },
  { key: 'receipt', label: 'Receipts' },
  { key: 'venue_photo', label: 'Memories' },
];

const CategorySheet = ({ photo, onCategorize, onClose }) => {
  if (!photo) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="category-sheet-overlay"
    >
      <div className="fixed inset-0 bg-black/40" />
      <div
        className="relative bg-white w-full sm:w-80 sm:rounded-xl rounded-t-2xl shadow-2xl z-10 pb-safe"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[#EAE8E3]">
          <h3 className="text-sm font-semibold text-[#1A1918]">Move to category</h3>
          <button onClick={onClose} className="p-1 hover:bg-[#F5F1EC] rounded-full" data-testid="close-category-sheet">
            <X weight="bold" className="w-4 h-4 text-[#7A7873]" />
          </button>
        </div>
        <div className="py-2">
          {PHOTO_CATEGORIES.map(c => (
            <button
              key={c.key}
              onClick={() => { onCategorize(photo.id, c.key); onClose(); }}
              className={`w-full text-left px-5 py-3 text-sm flex items-center justify-between hover:bg-[#F5F1EC] active:bg-[#EAE8E3] transition-colors ${
                c.key === photo.type ? 'text-[#C4A35A] font-semibold' : 'text-[#4A4845]'
              }`}
              data-testid={`cat-option-${c.key}`}
            >
              {c.label}
              {c.key === photo.type && <span className="text-xs text-[#C4A35A]">Current</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const PhotoThumb = ({ photo, catKey, idx, isCover, onLightbox, onSetCover, onOpenCategorySheet }) => {
  return (
    <div className="relative group aspect-square rounded-xl overflow-hidden ring-1 ring-[#EAE8E3]">
      <button onClick={onLightbox} className="w-full h-full" data-testid={`photo-thumb-${catKey}-${idx}`}>
        <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover" loading="lazy" />
      </button>
      {/* Always-visible action row on mobile, hover on desktop */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-1.5 bg-gradient-to-t from-black/60 to-transparent sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onSetCover(photo.url); }}
          className={`px-2 py-1 rounded-md text-[9px] font-medium ${
            isCover ? 'bg-[#C4A35A] text-white' : 'bg-black/50 text-white/90'
          }`}
          title="Set as cover"
          data-testid={`set-cover-${catKey}-${idx}`}
        >
          {isCover ? 'Cover' : 'Set cover'}
        </button>
        {photo.id && (
          <button
            onClick={(e) => { e.stopPropagation(); onOpenCategorySheet(photo); }}
            className="px-2 py-1 rounded-md bg-black/50 text-white/90 text-[9px] font-medium flex items-center gap-1"
            title="Move to category"
            data-testid={`categorize-${catKey}-${idx}`}
          >
            <ArrowsLeftRight weight="bold" className="w-2.5 h-2.5" />
            Move
          </button>
        )}
      </div>
    </div>
  );
};

const PhotoGallery = ({ restaurant, onSetCover, onCategorize, onUploadPhotos, onOpenLightbox }) => {
  const photoUploadRef = React.useRef(null);
  const [uploadingCategory, setUploadingCategory] = React.useState(null);
  const [categorySheetPhoto, setCategorySheetPhoto] = React.useState(null);

  const photosByCategory = {};
  const photosByCategoryAndDate = {};

  (restaurant?.visits || []).forEach(visit => {
    const visitDateStr = visit.visited_at
      ? new Date(visit.visited_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
      : 'Unknown date';

    (visit.attachments || []).forEach(att => {
      const cat = att.label || att.attachment_type || 'venue_photo';
      const normCat = ['food', 'drinks', 'menu', 'interior', 'exterior', 'receipt'].includes(cat) ? cat : 'venue_photo';
      const photoObj = {
        id: att.id,
        url: att.url
          ? `${process.env.REACT_APP_BACKEND_URL}${att.url.startsWith('/api') ? '' : '/api'}${att.url.startsWith('/api') ? att.url : att.url}`
          : att.file_path
            ? `${process.env.REACT_APP_BACKEND_URL}/api${att.file_path}`
            : null,
        caption: `${visit.meal_type || 'Visit'} - ${visitDateStr}`,
        type: normCat,
        visitDate: visitDateStr,
        visitTimestamp: visit.visited_at || '',
      };
      if (!photoObj.url) return;
      if (!photosByCategory[normCat]) photosByCategory[normCat] = [];
      photosByCategory[normCat].push(photoObj);

      if (!photosByCategoryAndDate[normCat]) photosByCategoryAndDate[normCat] = {};
      if (!photosByCategoryAndDate[normCat][visitDateStr]) photosByCategoryAndDate[normCat][visitDateStr] = [];
      photosByCategoryAndDate[normCat][visitDateStr].push(photoObj);
    });
  });

  const categoriesWithPhotos = PHOTO_CATEGORIES.filter(c => (photosByCategory[c.key] || []).length > 0);
  const allPhotos = categoriesWithPhotos.flatMap(c => photosByCategory[c.key] || []);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !uploadingCategory) return;
    await onUploadPhotos(files, uploadingCategory);
    setUploadingCategory(null);
    if (photoUploadRef.current) photoUploadRef.current.value = '';
  };

  return (
    <div className="card-elevated card-padding" data-testid="photo-gallery">
      <h2 className="font-heading text-xl font-semibold mb-5 flex items-center gap-2">
        <ImageIcon weight="duotone" className="w-5 h-5 text-[#A94438]" />
        Photos {allPhotos.length > 0 && `(${allPhotos.length})`}
      </h2>

      <input
        ref={photoUploadRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handlePhotoUpload}
        className="hidden"
        data-testid="category-photo-input"
      />

      {categoriesWithPhotos.map((cat) => {
        const catPhotos = photosByCategory[cat.key] || [];
        const dateGroups = photosByCategoryAndDate[cat.key] || {};
        const sortedDates = Object.keys(dateGroups).sort((a, b) => {
          const ta = dateGroups[a][0]?.visitTimestamp || '';
          const tb = dateGroups[b][0]?.visitTimestamp || '';
          return tb.localeCompare(ta);
        });
        let globalOffset = 0;
        for (const c of PHOTO_CATEGORIES) {
          if (c.key === cat.key) break;
          globalOffset += (photosByCategory[c.key] || []).length;
        }

        return (
          <div key={cat.key} className="mb-8 last:mb-0" data-testid={`photo-section-${cat.key}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[#1A1918] tracking-tight flex items-center gap-2">
                {cat.label}
                <span className="text-sm text-[#9A9590] font-normal">({catPhotos.length})</span>
              </h3>
              <button
                onClick={() => {
                  setUploadingCategory(cat.key);
                  setTimeout(() => photoUploadRef.current?.click(), 0);
                }}
                className="text-xs text-[#A94438] hover:underline flex items-center gap-1"
                data-testid={`add-photo-${cat.key}`}
              >
                <Plus weight="bold" className="w-3 h-3" />
                Add
              </button>
            </div>

            {sortedDates.map((dateStr) => {
              const datePhotos = dateGroups[dateStr];
              return (
                <div key={dateStr} className="mb-4 last:mb-0">
                  <p className="text-xs font-medium text-[#7A7873] mb-2 flex items-center gap-1.5 border-b border-[#EAE8E3] pb-1.5">
                    <CalendarBlank weight="duotone" className="w-3.5 h-3.5 text-[#9A9590]" />
                    {dateStr}
                    <span className="text-[#9A9590] font-normal">({datePhotos.length})</span>
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {datePhotos.map((photo, idx) => {
                      const catIdx = catPhotos.indexOf(photo);
                      const isCover = restaurant.hero_image_url && photo.url === restaurant.hero_image_url;
                      return (
                        <PhotoThumb
                          key={idx}
                          photo={photo}
                          catKey={cat.key}
                          idx={idx}
                          isCover={isCover}
                          onLightbox={() => onOpenLightbox(globalOffset + catIdx)}
                          onSetCover={onSetCover}
                          onOpenCategorySheet={setCategorySheetPhoto}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Quick-add for categories without photos */}
      {(restaurant?.visits || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#EAE8E3]">
          {PHOTO_CATEGORIES.filter(c => !(photosByCategory[c.key] || []).length).map((cat) => (
            <button
              key={cat.key}
              onClick={() => {
                setUploadingCategory(cat.key);
                setTimeout(() => photoUploadRef.current?.click(), 0);
              }}
              className="flex items-center gap-1.5 text-xs text-[#7A7873] hover:text-[#A94438] border border-dashed border-[#EAE8E3] hover:border-[#A94438]/30 rounded-lg px-3 py-2 transition-colors"
              data-testid={`add-first-photo-${cat.key}`}
            >
              <Camera weight="duotone" className="w-3.5 h-3.5" />
              Add {cat.label}
            </button>
          ))}
        </div>
      )}

      {allPhotos.length === 0 && (restaurant?.visits || []).length === 0 && (
        <p className="text-sm text-[#9A9590] text-center py-6">
          Check in or log a visit to start adding photos
        </p>
      )}

      {/* Category move sheet */}
      {categorySheetPhoto && (
        <CategorySheet
          photo={categorySheetPhoto}
          onCategorize={onCategorize}
          onClose={() => setCategorySheetPhoto(null)}
        />
      )}
    </div>
  );
};

export { PHOTO_CATEGORIES, PhotoGallery as default };
